import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import { User } from '../../../models/user';
import { UserState } from '../../../state/user/user-state';
import { HarloweSupport } from '../../../models/support';
import { Fax, FaxWrite } from '../../../models/fax';
import { IFaxHandler } from './fax-handler';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { DragDropModule } from 'primeng/dragdrop';
import { PickListModule } from 'primeng/picklist';
import { InputMaskModule } from 'primeng/inputmask';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { FaxService } from '../../../services/fax.service';
import { DividerModule } from 'primeng/divider';
import { ChipModule } from 'primeng/chip';
import { distinctUntilChanged, filter, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-fax-scroller',
  standalone: true,
  templateUrl: './fax-scroller.component.html',
  imports: [CommonModule, FormsModule, DialogModule, InputTextModule, InputTextareaModule, InputMaskModule, DropdownModule, DividerModule, ChipModule,
            ButtonModule, RippleModule, OverlayPanelModule, TableModule, TagModule, DragDropModule, PickListModule, CheckboxModule],
  styleUrls: ['./fax-scroller.component.scss']
})
export class FaxScrollerComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  currentUser$ = this.store.select(UserState.currentUser);
  currentUser: User | undefined = undefined;


  @Input() handler: IFaxHandler | undefined = undefined;
  @Input() faxes: Fax[] = [];
  files:  Map<string, HarloweSupport.File[]> = new Map();
  
  get fileEntryList(): [string, HarloweSupport.File[]][] 
  {
    if (this.handler?.getAttachments())
    {
      return Array.from(this.handler.getAttachments().entries());
    }
    return [];
  }
  
  get handlerReady(): boolean {
    return this.handler?.isReady ?? false;
  }

  selectedFilesToSend: string[] =[];
  selectedFax: Fax | undefined = undefined;;
  newFaxVisible: boolean = false;
  pendingFax: FaxWrite | undefined = undefined;
  selectedFaxType: string | undefined = undefined;
  faxTypeOptions = [
    { label: 'Reduce My Costs', value: 'rmc' },
    { label: 'Harlowe Health', value: 'harlowe' },
    { label: 'CGI', value: 'cgi' }
  ];

  constructor(
    private store: Store,
    private _faxService: FaxService
  ) { }


  // TODO: Pass Encounter into Handler with the sendFax logic

  ngOnInit(): void {
    this.currentUser$
      .pipe(
        filter((cu): cu is User => cu != null && cu.id != null),
        distinctUntilChanged((a, b) => a.id === b.id),
        takeUntil(this.destroy$)
      )
      .subscribe(
        u => this.currentUser = u
      );

    if (this.handler)
      this.files = this.handler.getAttachments();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  faxFiles(ids: string[]): HarloweSupport.File[] {
    var allFiles = [... this.files.values()].flat();
    return allFiles.filter(f => ids.includes(f.id.toString())) as HarloweSupport.File[]
  }

  newFax()
  {
    this.selectedFaxType = undefined;
    this.pendingFax = undefined;
    this.newFaxVisible = true;
  }

  faxTypeChanged()
  {
    if (!this.handler || !this.currentUser || !this.selectedFaxType)
    {
      this.pendingFax = undefined;
      return;
    }

    if (this.handler.isReady && this.selectedFaxType === 'cgi')
    {
      this.pendingFax = this.handler.newCgiFax(this.currentUser);
    }
    else if (this.handler.isReady)
    {
      this.pendingFax = this.handler.newFax(this.currentUser, this.selectedFaxType === 'rmc');
    }
  }

  cancelButton(event: any)
  {
    this.selectedFaxType = undefined;
    this.pendingFax = undefined;
    this.newFaxVisible = false;
    this.selectedFilesToSend = [];
  }
  
  async saveButton(event: any)
  {
    //Make sure the file ids are attached
    if (this.pendingFax)
    {
      this.pendingFax.attachmentIds = this.selectedFilesToSend.map(f => f.toString());
    }
    //Let this thing run in the background...
    var result = await this.sendFax();
    if(result)
    {
      this.pendingFax=undefined;
      this.newFaxVisible = false;
      this.selectedFilesToSend = [];
    }
  }

  get saveButtonActive() :boolean
  {
    if
    (
      this.selectedFilesToSend != null &&
      this.selectedFilesToSend.length > 0 && 
      this.pendingFax?.attn && this.pendingFax?.attn != null &&
      this.pendingFax?.faxNumber != null &&
      this.pendingFax?.faxNumber.length >= 10 &&
      this.pendingFax?.from != null &&
      this.pendingFax?.notes != null &&
      this.pendingFax.subject != null
    )
    {
      return true;
    }
    else
    {
      return false;
    }
  }

  previewFile(file: HarloweSupport.File)
  {
    // show popup with invoice PDF
    this.handler?.previewFile(file);
  }

  async sendFax() : Promise<boolean>
  {
    var fax = (await this.handler?.sendFax(this.pendingFax as FaxWrite));


    //Once we have sent the fax, we have a handle to refresh its status...
    if(fax)
    {
      this.faxes = [... this.faxes, fax]
      //Append the fax to the list, and continue to get its staus until it's complete, or failed
      this.updateFaxStatus(fax);
      return true;
    }
    else
    {
      return false;
    }
  } 

  async updateFaxStatus(fax: Fax)
  {
    var completed = false;
    while(!completed)
    {
      var updatedFax = await this._faxService.getFax(fax.id);
      if (updatedFax?.status === 'Successful' || updatedFax?.status === 'Failure') 
      {
          completed = true;
          this.faxes = this.faxes.map(f => {if (updatedFax && f.id == updatedFax.id) {return updatedFax} else {return f}})
          this.faxes = [... this.faxes]
      }
      else
      {
          await new Promise((resolve, reject) => setTimeout(resolve, 5000));
      }  
    }

  }

  trackByLabel(index: number, item: [string, HarloweSupport.File[]])
  {
    return item[0];
  }

  trackByField(index: number, file: HarloweSupport.File)
  {
    return file.id;
  }

}
